import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import fileService from "../services/fileService";
import axios from "axios";

interface FileDownloadCompRef {
    downloadFile: (path: string, defaultFileName?: string) => void;
}

const FileDownloadComp = forwardRef<FileDownloadCompRef>(
    (props, ref) => {

        const [errorMessage, setErrorMessage] = useState<string>("");
        const [progress, setProgress] = useState<number>(0);
        const [cancelTokenSource, setCancelTokenSource] = useState<any>(null);
        const [isDownloading, setIsDownloading] = useState<boolean>(false);

        const modalRef = useRef<HTMLDivElement | null>(null)
        const modelInstanceRef = useRef<any>(null);

        useEffect(() => {
            if (modalRef.current) {
                modelInstanceRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            }
        }, []);

        const startDownload = (url: string) => {
            setErrorMessage("");
            setProgress(0);
            modelInstanceRef.current?.show();
            downloadFile(url);
        }

        const onProgress = (event: ProgressEvent) => {
            const percentCompleted = Math.round((event.loaded * 100) / event.total);
            setProgress(percentCompleted);
        }

        const downloadFile = async (path: string, defaultFileName?: string) => {
            if (!isDownloading) {
                setIsDownloading(true);
                try {
                    const cancelToken = fileService.getCancelToken();
                    setCancelTokenSource(cancelToken);
                    const response = await fileService.downloadFile(path, onProgress, cancelToken);
                    // get original file name from content-disposition header
                    let fileName = defaultFileName || 'file';
                    const disposition = response.headers["content-disposition"];
                    if (disposition) {
                        // Match filename= up to semicolon or end of string
                        const fileNameMatch = disposition.match(/filename=([^;]+)/);
                        if (fileNameMatch && fileNameMatch[1]) {
                            // Trim whitespace and remove quotes if present
                            fileName = fileNameMatch[1].trim().replace(/(^"|"$)/g, "");
                        }
                    }
                    const fileBlob = new Blob([response.data]);
                    const downloadUrl = window.URL.createObjectURL(fileBlob);
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode?.removeChild(link);
                    setTimeout(() => {
                        modelInstanceRef.current?.hide();
                    }, 500);
                } catch (error) {
                    if (axios.isCancel(error)) {
                        setErrorMessage("Download cancelled.");
                    } else {
                        setErrorMessage("Error downloading file. Please try again.");
                    }
                } finally {
                    setIsDownloading(false);
                }
            }
        }

        const cancelDownload = () => {
            if (cancelTokenSource) {
                cancelTokenSource.cancel("Download cancelled by user.");
            }
            setIsDownloading(false);
            setErrorMessage("");
            setProgress(0);
            modelInstanceRef.current?.hide();
        }

        useImperativeHandle(ref, () => ({
            downloadFile: (path: string) => startDownload(path)
        }));

        return (
            <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
                <div className="modal-dialog">
                    <div className="modal-content rounded-0">
                        <div className="modal-header">
                            <h5 className="modal-title">Downloading File</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={cancelDownload}
                            ></button>
                        </div>
                        <div className="modal-body">
                            {errorMessage && errorMessage !== "" ? (
                                <div className="alert alert-danger" role="alert">{errorMessage}</div>
                            ) : (
                                <div className="progress mt-3 mb-3">
                                    <div className="progress-bar progress-bar-striped bg-success" role="progressbar" style={{ width: `${progress}%` }} aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div >
            </div >
        );
    }
);

export default FileDownloadComp;
export type { FileDownloadCompRef };